<template>
  <app-page class="ver-page" fill lean>
    <!-- Aufbau wie die übrigen Tabellenseiten (Kundenwunsch 13.10.2026): leichter Titel, Suche links; ohne Filter und
         Gruppierung stehen die Aktionen rechts in der Werkzeugleiste (Handy: Icon-Knöpfe im Kopf). Der Upload steht im
         Dialog „Fahrplan hochladen“ statt als Karte neben der Tabelle -->
    <template v-if="handy" #actions>
      <app-icon-button icon="refresh" label="Aktualisieren" @click="refresh" />
      <app-button icon="upload" @click="uploadDialog = true">Hochladen</app-button>
    </template>
    <template #toolbar>
      <app-search v-model="searchText" placeholder="In Fahrplänen suchen" @input="onSearchInput" />
    </template>
    <template v-if="!handy" #toolbar-end>
      <app-button icon="refresh" @click="refresh">Aktualisieren</app-button>
      <app-button icon="upload" @click="uploadDialog = true">Fahrplan hochladen</app-button>
    </template>

    <app-dialog v-model="uploadDialog" title="Fahrplan hochladen" :max-width="560" nested blur>
      <BconnFileUploader />
      <template #actions>
        <app-button @click="uploadDialog = false">Schließen</app-button>
      </template>
    </app-dialog>

    <!-- Tabelle und Fußleiste links, rechts daneben das Import-Log der gewählten Version im Detailbereich wie bei
         Verkäufe (Kundenwunsch 13.10.2026) -->
    <div class="ver-gridcol">
      <!-- Tabelle füllt die Höhe über der Fußleiste (Handy: natürliche Höhe) -->
      <div class="app-grid-fill ver-grid" :class="{ 'ver-grid--auto': handy }">
        <VersionImportDataGrid
          ref="importGrid"
          :dataSource="dataSource"
          :columns="columns"
          :height="gridHeight"
          @select="onSelect"
          @content-ready="onContentReady"
        ></VersionImportDataGrid>
      </div>
      <app-grid-footer ref="fuss" :page-sizes="pageSizes">
        <template #summary>
          <span v-if="narrowed"><strong>{{ summaryCount }}</strong> von {{ summaryLoaded }}</span>
          <span v-else><strong>{{ summaryCount }}</strong> {{ summaryNoun }}</span>
        </template>
      </app-grid-footer>
    </div>

    <template #detail>
      <app-detail-pane
        :open="!!selectedVersion"
        wide
        icon="file"
        title="Fahrplanversion"
        close-label="Import-Log schließen"
        @close="closeDetail"
        @layout-change="onDetailLayout"
      >
        <template #subtitle>
          <span v-if="selectedVersion">{{ versionText(selectedVersion) }}</span>
        </template>
        <!-- Zusatzinfos, Downloads und Import-Log (Kundenwunsch 13.10.2026) -->
        <version-detail
          v-if="selectedVersion"
          :key="selectedVersion.version_id"
          :version="selectedVersion"
          :downloads="downloads"
          @download="onDownload($event, selectedVersion)"
        />
      </app-detail-pane>
    </template>
  </app-page>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";

import CustomStore from "devextreme/data/custom_store";
import VersionImportDataGrid from "@/components/VersionImportDataGrid";
import BconnFileUploader from "@/components/BconnFileUploader";
import VersionDetail from "@/components/versions/VersionDetail";
import websocket from "@/aws/websocket";
import DataGridMixin from "@/mixins/DataGridMixin";
import { formatNumber, plural } from "@/utils/format";

export default {
  components: { VersionImportDataGrid, BconnFileUploader, VersionDetail },
  mixins: [DataGridMixin],
  computed: {
    ...mapState(["profile"]),
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    // Formate im Download-Menü: immer der Fahrplan als ZIP, GTFS und VDV nur, wenn für den Kunden freigeschaltet
    downloads() {
      const customer = (this.profile && this.profile.customer) || {};
      const items = [{ key: "zip", text: "Fahrplan (ZIP)" }];
      if (customer.enable_gtfs_download != 0) {
        items.push({ key: "gtfs", text: "GTFS" });
      }
      if (customer.enable_vdv_download != 0) {
        items.push({ key: "vdv", text: "VDV" });
      }
      return items;
    },
    // Handy: AppPage fill wirkt dort nicht, das Grid wächst mit seinen Zeilen; sonst füllt es die Höhe
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    // Fußleiste: Anzahl nach der Suche, „3 von 12 Importen“ solange eingeschränkt
    narrowed() {
      return this.summary.count !== this.summary.loaded;
    },
    summaryCount() {
      return formatNumber(this.summary.count);
    },
    summaryLoaded() {
      return formatNumber(this.summary.loaded) + " " + plural(this.summary.loaded, "Import", "Importen");
    },
    summaryNoun() {
      return plural(this.summary.count, "Import", "Importe");
    },
    columns() {
      return [
          {
          dataField: "import_status",
          caption: "Import Status",
          cellTemplate: "importStatusCellTemplate",
        },
        {
          dataField: "version_id",
          caption: "Versions ID",
          sortOrder: "desc",
          cellTemplate: "versionIdCellTemplate",
          sortingMethod: function (val1, val2) {
            let value1 = val1;
            let value2 = val2;
            if (val1 < 0) value1 = 1000000000 - val1;
            if (val2 < 0) value2 = 1000000000 - val2;
            if (!value1 && value2) return -1;
            if (!value1 && !value2) return 0;
            if (value1 && !value2) return 1;
            if (value1 > value2) return 1;
            if (value2 > value1) return -1;
            if (value1 == value2) return 0;
            return 0;
          },
        },
        {
          dataField: "valid_from",
          caption: "Gültig ab",
          dataType: "date",
        },
        {
          dataField: "stage",
          caption: "Stage",
        },
        {
          dataField: "creation_time",
          caption: "Erstellt",
          dataType: "datetime",
        },
         {
          dataField: "creation_username",
          caption: "Erstellt von"        
          },
           {
          dataField: "modification_time",
          caption: "Geändert",
          dataType: "datetime",
        },
        {
          dataField: "modification_username",
          caption: "Geändert von"
        },
      ];
    },
  },
  data() {
    return {
      searchText: "",
      uploadDialog: false,
      // gewählte Version (eingefrorene Kopie): Import-Log im Detailbereich
      selectedVersion: null,
      summary: { count: 0, loaded: 0 },
      dataSource: {
        store: new CustomStore({
          key: "version_id",
          load: () => axios.get(`${this.$store.state.baseUrl}version`).then((response) => {
            this._rows = Array.isArray(response.data) ? response.data : [];
            return response;
          }),
        }),
        reshapeOnPush: true,
      },
    };
  },
  methods: {
    refresh() {
      this.$refs.importGrid.refreshDataGrid();
    },
    onSelect(row) {
      this.selectedVersion = Object.freeze(Object.assign({}, row));
    },
    closeDetail() {
      this.selectedVersion = null;
      this.$refs.importGrid.clearFocus();
    },
    onDetailLayout() {
      this.$nextTick(() => this.$refs.importGrid && this.$refs.importGrid.updateDimensions());
    },
    // Untertitel „#51“ (unbekannte Version „?“ wie in der Tabelle)
    versionText(row) {
      return "#" + (row.version_id < 0 ? "?" : row.version_id);
    },
    onSearchInput() {
      this.$refs.importGrid.searchByText(this.searchText);
    },
    onContentReady(e) {
      const grid = e.component;
      const count = Math.max(Number(grid.totalCount()) || 0, 0);
      // ohne Suche zählt das Grid alle (auch per Websocket nachgeschobene); mit Suche die geladenen Zeilen
      const loaded = this.searchText ? Math.max((this._rows || []).length, count) : count;
      this.summary = { count, loaded };
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
    },
    // Download-Menü: Formate auf die bisherigen Aufrufe abbilden (diese erwarten { row: { data } })
    onDownload(key, row) {
      const e = { row: { data: row } };
      if (key === "gtfs") {
        this.downloadGtfs(e);
      } else if (key === "vdv") {
        this.downloadVdv(e);
      } else {
        this.download(e);
      }
    },
    downloadGtfs(e) {
      const versionId = e.row.data.version_id;
      var url = `https://s3i3yhy52h.execute-api.eu-central-1.amazonaws.com/v1/gtfs?customer_id=${this.$store.state.customerId}&version_id=${versionId}&forceUpdate=1`;
      window.open(url);
    },
    downloadVdv(e) {
      const versionId = e.row.data.version_id;
      var url = `https://bfgal5h1c7.execute-api.eu-central-1.amazonaws.com/v1/vdv?customer_id=${this.$store.state.customerId}&version_id=${versionId}&forceUpdate=1`;
      window.open(url);
    },
    async download(e) {
      const downloadItem = e.row.data;
      const downloadInfo = (
        await axios.post(
          `${this.$store.state.baseUrl}version?version_id=${downloadItem.version_id}`
        )
      ).data;
      window.open(downloadInfo.downloadUrl);
    },
  },
  async mounted() {
    websocket.subscribe("version-import", (message) => {
      if (message.data.type === "INSERT") {
        this.dataSource.store.push([
          { type: "insert", data: message.data.data },
        ]);
      } else if (message.data.type === "MODIFY") {
        this.dataSource.store.push([
          {
            type: "update",
            key: message.data.key.version_id,
            data: message.data.data,
          },
        ]);
      } else if (message.data.type === "REMOVE") {
        this.dataSource.store.push([
          { type: "remove", key: message.data.key.version_id },
        ]);
      }
    });
  },
  destroyed() {
    websocket.unsubscribe("version-import");
  },
};
</script>

<style>
#dropzone {
  border: none !important;
}
</style>

<style scoped>
/* Tabelle und Fußleiste als Spalte links vom Detailbereich (wie Verkäufe) */
.ver-gridcol {
  flex: 999 1 560px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
/* Handy: Grid in natürlicher Höhe */
.ver-grid.ver-grid--auto {
  flex: none;
}
</style>